/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isPlainObject from '@stdlib/assert-is-plain-object';
import hasOwnProp from '@stdlib/assert-has-own-property';
import { factory as contains } from '@stdlib/array-base-assert-contains';
import join from '@stdlib/array-base-join';
import format from '@stdlib/error-tools-fmtprodmsg';
import linearFormat from './format_linear.js';
import layoutFormat from './format_layout.js';


// VARIABLES //

var FORMATS = [
	'none',
	'linear',
	'layout'
];
var isFormat = contains( FORMATS );


// MAIN //

/**
* Serializes a struct to a string.
*
* @private
* @param {Function} Struct - struct constructor
* @param {Array<Object>} fields - list of normalized fields
* @param {Options} options - function options
* @param {string} [options.format] - serialization format
* @throws {TypeError} options argument must be an object
* @throws {TypeError} must provide valid options
* @returns {string} string representation
*/
function toString( Struct, fields, options ) { // eslint-disable-line stdlib/no-redeclare
	var opts;
	if ( !isPlainObject( options ) ) {
		throw new TypeError( format( '2KJ2V', options ) );
	}
	opts = {
		'format': 'none'
	};
	if ( hasOwnProp( options, 'format' ) ) {
		opts.format = options.format;
		if ( !isFormat( opts.format ) ) {
			throw new TypeError( format( '2KJ4S', 'format', join( FORMATS, ', ' ), options.format ) );
		}
	}
	if ( opts.format === 'linear' ) {
		return linearFormat( Struct, fields );
	}
	if ( opts.format === 'layout' ) {
		return layoutFormat( fields );
	}
	// Case: opts.format === 'none'
	return '<Struct>';
}


// EXPORTS //

export default toString;
